/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import apiService from "services/apiService";

/* ────────────────────────────────────────────────────────────
   Holiday / Leave data for the Timeline.

   NOTE: the mapping mirrors pages/timesheet/index.tsx (same endpoints, same
   "rejected leaves are hidden" rule, same title fields) so both screens agree
   on what counts as a holiday or a leave. The difference is the shape: the
   calendar needs events, the Timeline needs a per-date lookup, so ranges are
   expanded into YYYY-MM-DD keys here.

   Everything degrades to an empty map when an endpoint fails or returns
   nothing, so the Timeline keeps rendering activity as before.
──────────────────────────────────────────────────────────── */

export type TimelineMarkerKind = "HOLIDAY" | "LEAVE";

export interface TimelineMarker {
    kind: TimelineMarkerKind;
    id?: string | number;
    /** Occasion (holiday) or leave type. */
    title: string;
    /** The day this marker was placed on (YYYY-MM-DD), shown in tooltips. */
    date: string;
    /** true for a 0.5 day leave. */
    halfDay?: boolean;
    status?: string;
}

export type TimelineMarkerMap = Record<string, TimelineMarker[]>;

/** Raw holiday row, as returned by GET /api/holiday/{year}. */
export interface HolidayRow {
    holidayId?: string | number;
    occasion?: string;
    holidayDate?: string | number;
    [key: string]: any;
}

/** Raw leave row, as returned by GET /api/leave/data/{year}?email=… */
export interface LeaveRow {
    id?: string | number;
    leaveFrom?: string | number;
    leaveTo?: string | number;
    leaveType?: string;
    /** Number of days: 0.5 for a half day. */
    unit?: number | string;
    /** Form-side equivalent of `unit` (1 = full day, 0.5 = half day). */
    leaveFor?: number | string;
    status?: string;
    leaveStatus?: string;
    leave_status?: string;
    [key: string]: any;
}

const MAX_LEAVE_DAYS = 400; // guard against a malformed range looping forever

const DATE_KEY_FORMAT = "YYYY-MM-DD";

/**
 * Accepted string forms, parsed strictly. Same list util/Util.tsx#dateFormate
 * uses, so a date the rest of the app can read is a date this can read.
 */
const STRING_FORMATS: any[] = [DATE_KEY_FORMAT, "D-MM-YYYY", "DD-MM-YYYY", moment.ISO_8601];

/**
 * Epochs and timestamps are read in LOCAL time, the same way
 * pages/timesheet/index.tsx reads them (`new Date(holiday.holidayDate)`), so
 * both screens place a holiday on the same calendar day. The seconds-vs-
 * milliseconds rule matches util/epochTime.ts#getUnixTime.
 *
 * NOTE: this is what put a holiday on the wrong row. A backend that serialises
 * an IST calendar date as an instant sends "…T18:30:00Z" (midnight IST); using
 * the "2026-09-01" text prefix of that value moved the holiday one day earlier,
 * so e.g. a 3 Sep holiday showed up on 2 Sep. Converting instead of slicing
 * keeps the day correct.
 */
const epochToDateKey = (epoch: number): string | null => {
    if (!Number.isFinite(epoch)) return null;

    const value = Math.trunc(epoch);
    const milliseconds = Math.abs(value) > 1e10 ? value : value * 1000;
    const parsed = moment(milliseconds);

    return parsed.isValid() ? parsed.format(DATE_KEY_FORMAT) : null;
};

/**
 * Anything that cannot be read confidently returns null and the marker is
 * dropped: a marker on the wrong day is worse than a missing one.
 */
export const toDateKey = (value: unknown): string | null => {
    if (value === null || value === undefined || value === "") return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : moment(value).format(DATE_KEY_FORMAT);
    }

    if (typeof value === "number") return epochToDateKey(value);

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;

        // A plain calendar date carries no timezone, so it is used verbatim
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

        if (/^-?\d{10}$|^-?\d{13}$/.test(trimmed)) return epochToDateKey(Number(trimmed));

        // Anything with a time component is converted, never sliced (see above)
        const parsed = moment(trimmed, STRING_FORMATS, true);
        return parsed.isValid() ? parsed.format(DATE_KEY_FORMAT) : null;
    }

    return null;
};

/** Same rule as the timesheet calendar: hide rejected leaves, keep the rest. */
const isVisibleLeave = (leave: LeaveRow) => {
    const rawStatus = leave?.status ?? leave?.leaveStatus ?? leave?.leave_status ?? "";
    return String(rawStatus).trim().toLowerCase() !== "rejected";
};

const isHalfDayLeave = (leave: LeaveRow) => {
    const unit = leave?.leaveFor ?? leave?.unit;
    return Number(unit) === 0.5;
};

const getRowsFromPayload = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
};

/** Identity of a marker on a day, used to drop duplicate API rows. */
const markerSignature = (marker: TimelineMarker) =>
    `${marker.kind}|${marker.id ?? ""}|${marker.title}|${marker.halfDay ? "half" : "full"}`;

const addMarker = (markers: TimelineMarkerMap, marker: TimelineMarker) => {
    const existing = markers[marker.date] || (markers[marker.date] = []);
    if (existing.some((entry) => markerSignature(entry) === markerSignature(marker))) return;
    existing.push(marker);
};

/** Holidays → { "YYYY-MM-DD": [marker] } */
export const mapHolidaysToMarkers = (payload: any): TimelineMarkerMap => {
    const markers: TimelineMarkerMap = {};

    getRowsFromPayload(payload).forEach((holiday: HolidayRow) => {
        const dateKey = toDateKey(holiday?.holidayDate);
        if (!dateKey) return;

        addMarker(markers, {
            kind: "HOLIDAY",
            id: holiday?.holidayId,
            title: holiday?.occasion || "Holiday",
            date: dateKey,
        });
    });

    return markers;
};

/** Leaves → { "YYYY-MM-DD": [marker] }, one entry per day of the range. */
export const mapLeavesToMarkers = (payload: any): TimelineMarkerMap => {
    const markers: TimelineMarkerMap = {};

    getRowsFromPayload(payload)
        .filter(isVisibleLeave)
        .forEach((leave: LeaveRow) => {
            const fromKey = toDateKey(leave?.leaveFrom);
            const toKey = toDateKey(leave?.leaveTo) || fromKey;
            if (!fromKey || !toKey) return;

            const cursor = moment(fromKey, DATE_KEY_FORMAT, true);
            const last = moment(toKey, DATE_KEY_FORMAT, true);
            if (!cursor.isValid() || !last.isValid() || last.isBefore(cursor, "day")) return;

            const halfDay = isHalfDayLeave(leave);
            const status = leave?.status ?? leave?.leaveStatus ?? leave?.leave_status;

            // Expand the (inclusive) range so every affected day is marked
            for (let i = 0; i <= MAX_LEAVE_DAYS && cursor.isSameOrBefore(last, "day"); i += 1) {
                addMarker(markers, {
                    kind: "LEAVE",
                    id: leave?.id,
                    title: leave?.leaveType || "Leave",
                    date: cursor.format(DATE_KEY_FORMAT),
                    halfDay,
                    status,
                });
                cursor.add(1, "day");
            }
        });

    return markers;
};

const mergeMarkerMaps = (maps: TimelineMarkerMap[]): TimelineMarkerMap =>
    maps.reduce<TimelineMarkerMap>((merged, map) => {
        Object.keys(map).forEach((dateKey) => {
            map[dateKey].forEach((marker) => addMarker(merged, marker));
        });
        return merged;
    }, {});

interface UseTimelineCalendarOptions {
    /** Employee email; leaves are skipped when it is missing. */
    email?: string | null;
    /** Years covered by the visible range (a range can straddle a year end). */
    years: number[];
    /** Skipped when the caller already supplies holiday rows. */
    fetchHolidays?: boolean;
    /** Skipped when the caller already supplies leave rows. */
    fetchLeaves?: boolean;
}

/**
 * Loads holidays (per year) and leaves (per year, per employee) and returns
 * per-date lookups. Failures are swallowed into empty maps by design — the
 * Timeline must still render activity if these endpoints are unavailable.
 */
export const useTimelineCalendar = ({
    email,
    years,
    fetchHolidays = true,
    fetchLeaves = true,
}: UseTimelineCalendarOptions) => {
    const [holidayMarkers, setHolidayMarkers] = useState<TimelineMarkerMap>({});
    const [leaveMarkers, setLeaveMarkers] = useState<TimelineMarkerMap>({});

    // Stable dependency: the effects re-run only when the actual years change
    const yearKey = useMemo(() => years.filter(Boolean).sort().join(","), [years]);

    useEffect(() => {
        const yearList = yearKey ? yearKey.split(",").map(Number).filter(Boolean) : [];

        if (!fetchHolidays || yearList.length === 0) {
            setHolidayMarkers({});
            return;
        }

        let cancelled = false;

        Promise.all(
            yearList.map((year) =>
                apiService
                    .get(`/api/holiday/${year}`)
                    .then((res) => mapHolidaysToMarkers(res?.data))
                    .catch(() => ({} as TimelineMarkerMap))
            )
        ).then((maps) => {
            if (!cancelled) setHolidayMarkers(mergeMarkerMaps(maps));
        });

        return () => {
            cancelled = true;
        };
    }, [fetchHolidays, yearKey]);

    useEffect(() => {
        const yearList = yearKey ? yearKey.split(",").map(Number).filter(Boolean) : [];

        if (!fetchLeaves || !email || yearList.length === 0) {
            setLeaveMarkers({});
            return;
        }

        let cancelled = false;

        Promise.all(
            yearList.map((year) =>
                apiService
                    .get(`/api/leave/data/${year}?email=${encodeURIComponent(email)}`)
                    .then((res) => mapLeavesToMarkers(res?.data))
                    .catch(() => ({} as TimelineMarkerMap))
            )
        ).then((maps) => {
            if (!cancelled) setLeaveMarkers(mergeMarkerMaps(maps));
        });

        return () => {
            cancelled = true;
        };
    }, [fetchLeaves, email, yearKey]);

    return { holidayMarkers, leaveMarkers };
};

/** Combines both lookups into one per-date list (holidays first). */
export const buildMarkerMap = (
    holidayMarkers: TimelineMarkerMap,
    leaveMarkers: TimelineMarkerMap
): TimelineMarkerMap => mergeMarkerMaps([holidayMarkers, leaveMarkers]);
